/**
 * Volt-to-RRR adapter.
 *
 * Converts a Volt RouteDefinition + Entrypoint pair into an
 * RRR-compatible HomepageRouteType. Reuses createSiteRoute and
 * createGlobalRoute for path expansion, so no routing behaviour changes.
 *
 * Component slots are wrapped in Loadable() to produce code-split
 * components with .preload(). When Relay entrypoints land, slots
 * become JSResourceReferences and this wrapping goes away.
 */

import type { ComponentType } from 'react';

import Loadable from '@townsquare/loadable';
import type { LoadingComponentProps } from '@townsquare/loadable/types';

import { createGlobalRoute, createSiteRoute } from '../home/create-route';
import type { HomepageRoute, HomepageRouteType } from '../home/types';

import type { ComponentSlot, Entrypoint, RouteDefinition } from './types';

/**
 * Wraps a ComponentSlot in Loadable for RRR compatibility.
 * Produces a component with .preload() for code-split loading.
 *
 * The loading fallback type doesn't align with react-loadable's
 * LoadingComponentProps. Both mismatches are safe: Loadable forwards
 * props to the loaded component at runtime, and the loading component
 * ignores LoadingComponentProps it doesn't use.
 */
function wrapSlot(slot: ComponentSlot) {
	return Loadable({
		loader: slot.loader,
		loading: (slot.loading ?? (() => null)) as ComponentType<LoadingComponentProps>,
	});
}

export function toRRRRoute(def: RouteDefinition, entrypoint: Entrypoint): HomepageRouteType {
	// The cast is intentional: Loadable returns ComponentType<inferred> & LoadableComponent,
	// but HomepageRoute expects ComponentType<RouteContext> & LoadableComponent.
	// At runtime the Loadable wrapper passes all props through to the loaded component,
	// so the RouteContext props are forwarded correctly.
	const base = {
		name: def.name,
		path: def.pattern,
		exact: true,
		component: wrapSlot(entrypoint.root),
		aside: entrypoint.aside ? wrapSlot(entrypoint.aside) : undefined,
		header: entrypoint.header ? wrapSlot(entrypoint.header) : undefined,
		aggContainer: entrypoint.aggContainer ? wrapSlot(entrypoint.aggContainer) : undefined,
		resources: entrypoint.resources,
		layout: def.layout,
		nav4Layout: def.nav4Layout,
		embed: def.embed,
		capabilities: def.capabilities,
		enableRestrictedFallback: def.enableRestrictedFallback,
		subproduct: def.subproduct,
	} as HomepageRoute;

	return def.scope === 'site' ? createSiteRoute(base) : createGlobalRoute(base);
}
